import {Link, useLocation, useNavigate} from 'react-router-dom';
import {LoaderSpinner} from '@/shared/ui/page-loader';
import {Input} from '@/shared/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import {useAuth} from '@/contexts/AuthContext';
import {useToast} from '@/hooks/use-toast';
import {projectsApi} from '@/features/projects/services';
import {schedulerApi} from '@/features/scheduler/services';
import {Project} from '@/shared/types/api';
import {getProjectPermissionFlags} from '@/shared/lib/project-permissions';
import {canAccessMasterInventory} from '@/shared/lib/master-inventory-access';
import {
  isModeratorSessionAccessRestricted,
  isModeratorTaskNavRestricted,
  withoutModeratorTaskNav,
} from '@/shared/lib/moderator-session-access';
import {
  isPaymentApprovalsEnabled,
  isProjectEmailTemplatesEnabled,
  isProjectConfigurationEnabled,
} from '@/config/env';
import {useEffect, useMemo, useRef, useState} from 'react';
import {useQueryClient} from '@tanstack/react-query';
import {useProjectsListQuery, useActionCenterCount} from '@/features/projects/hooks';
import {Badge} from '@/shared/ui/badge';
import {queryKeys} from '@/lib/query-keys';
import {clearProjectRoleCache, useProjectRole} from '@/hooks/use-project-role';
import {clearAllCached} from '@/shared/lib/localStorageCache';
import {
  Home,
  FolderOpen,
  Inbox,
  CheckSquare,
  Users,
  UserCheck,
  FileText,
  MessageSquare,
  Calendar,
  BarChart3,
  LogOut,
  BookOpen,
  Upload,
  Shield,
  Settings,
  X,
  ChevronDown,
  ChevronRight,
  Plus,
  HelpCircle,
  User,
  Database,
  DollarSign,
  Mail,
  Bot,
  PanelLeftClose,
  PanelLeftOpen,
  Package,
} from 'lucide-react';
import {cn} from '@/lib/utils';
import UploadStatusIndicator from '@/features/uploads/components/UploadStatusIndicator';
import {useProjectsApi, useProjectsData} from '@/contexts/ProjectsContext';

type UserRole = 'admin' | 'project_manager' | 'moderator' | 'scheduler' | 'participant';

// Role cache to store pre-fetched project roles for instant access
type RoleCache = Map<string, {role: string | null; hasAccess: boolean; loading: boolean}>;

// Custom hook to get project role with caching for instant access
/** A primary sidebar link, highlighted when it is the current route. */
const SidebarNavLink = ({
  to,
  icon: Icon,
  label,
  currentPath,
  onNavigate,
}: Readonly<{
  to: string;
  icon: React.ComponentType<{className?: string}>;
  label: string;
  currentPath: string;
  onNavigate: () => void;
}>) => (
  <Link
    to={to}
    onClick={onNavigate}
    className={cn(
      'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground',
      currentPath === to
        ? 'bg-accent text-accent-foreground'
        : 'text-muted-foreground'
    )}
  >
    <Icon className="h-4 w-4" />
    {label}
  </Link>
);

/** Status dot beside a project in the collapsed rail. */
const railStatusColor = (status?: string) => {
  switch (String(status || '').toLowerCase()) {
    case 'active':
      return 'bg-green-500';
    case 'draft':
      return 'bg-yellow-500';
    case 'completed':
      return 'bg-blue-500';
    default:
      return 'bg-gray-400';
  }
};

interface CollapsedRailProps {
  onToggleCollapse?: () => void;
  setIsMobileMenuOpen: (open: boolean) => void;
  location: {pathname: string};
  showProjectsSection: boolean;
  projects: Project[];
  getCurrentProjectId: () => string | null | undefined;
  navigate: (to: string) => void;
  setProjectHandler: (project: Project) => void;
}

/**
 * The sidebar in its collapsed state — an icon rail with a project dot per
 * project. Kept out of the navigation component so its branches are measured
 * (and read) on their own.
 */
const CollapsedRail = ({
  onToggleCollapse,
  setIsMobileMenuOpen,
  location,
  showProjectsSection,
  projects,
  getCurrentProjectId,
  navigate,
  setProjectHandler,
}: Readonly<CollapsedRailProps>) => (
  <div className="flex flex-1 flex-col items-center gap-1.5 overflow-y-auto overflow-x-hidden py-3">
    {/* Expand toggle */}
    <button
      type="button"
      onClick={() => onToggleCollapse?.()}
      title="Expand sidebar"
      aria-label="Expand sidebar"
      className="flex size-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
    >
      <PanelLeftOpen className="h-5 w-5" />
    </button>

    {/* Home */}
    <Link
      to="/"
      title="Home"
      onClick={() => setIsMobileMenuOpen(false)}
      className={cn(
        'flex size-9 items-center justify-center rounded-lg transition-colors',
        location.pathname === '/'
          ? 'bg-accent text-accent-foreground'
          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
      )}
    >
      <Home className="h-4 w-4" />
    </Link>

    {showProjectsSection && (
      <>
        <div className="my-1 h-px w-6 bg-border" aria-hidden="true" />
        {/* Project dots — click to switch project while collapsed */}
        {projects.map((p) => (
          <CollapsedRailProjectDot
            key={p.id}
            project={p}
            active={getCurrentProjectId() === p.id}
            navigate={navigate}
            setProjectHandler={setProjectHandler}
            setIsMobileMenuOpen={setIsMobileMenuOpen}
          />
        ))}
      </>
    )}
  </div>
);

/** One project dot in the collapsed rail. */
const CollapsedRailProjectDot = ({
  project,
  active,
  navigate,
  setProjectHandler,
  setIsMobileMenuOpen,
}: Readonly<{
  project: Project;
  active: boolean;
  navigate: (to: string) => void;
  setProjectHandler: (project: Project) => void;
  setIsMobileMenuOpen: (open: boolean) => void;
}>) => {
  const initial =
    String(project.name || '?').trim().charAt(0).toUpperCase() || '?';
  return (
    <button
      type="button"
      title={project.name}
      aria-label={project.name}
      onClick={() => {
        navigate(
          `/projects?projectId=${encodeURIComponent(project.id)}&tab=overview`
        );
        setProjectHandler(project);
        setIsMobileMenuOpen(false);
      }}
      className={cn(
        'relative flex size-9 shrink-0 items-center justify-center rounded-lg text-xs font-semibold transition-colors',
        active
          ? 'bg-accent text-accent-foreground ring-2 ring-primary/40'
          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
      )}
    >
      {initial}
      <span
        className={cn(
          'absolute bottom-1 right-1 size-2 rounded-full ring-2 ring-card',
          railStatusColor(project.status)
        )}
        aria-hidden="true"
      />
    </button>
  );
};

const useCachedProjectRole = (projectId: string, roleCache: RoleCache, project?: Project, cacheUpdateTrigger?: number) => {
  const {user} = useAuth();
  // Pass `project` so useProjectRole resolves the team-based user_role (e.g.
  // moderator) instead of falling through to creator-PM optimism. Without
  // this, a project creator who was later assigned a moderator role would
  // appear as PM in the sidebar but be denied at the page-level gate.
  const {role: projectRole, loading: roleLoading, hasAccess} = useProjectRole(projectId, project);
  const [cachedRole, setCachedRole] = useState<{role: string | null; hasAccess: boolean; loading: boolean} | null>(() => roleCache.get(projectId) || null);
  const buildRoleState = (role: string | null, hasAccessValue: boolean, loadingValue: boolean) => ({
    role,
    hasAccess: hasAccessValue,
    loading: loadingValue,
    ...getProjectPermissionFlags({
      projectRole: role,
      roleLoading: loadingValue,
      userRole: user?.role,
      userEmail: user?.email,
    }),
  });
  
  // Check cache first for instant access
  const cached = roleCache.get(projectId);
  
  // Update local state when cache changes (including when trigger updates)
  useEffect(() => {
    const currentCached = roleCache.get(projectId);
    if (currentCached) {
      // Compare by value, not reference
      const isDifferent = !cachedRole || 
        cachedRole.role !== currentCached.role ||
        cachedRole.hasAccess !== currentCached.hasAccess ||
        cachedRole.loading !== currentCached.loading;
      
      if (isDifferent) {
        setCachedRole(currentCached);
      }
    } else if (cachedRole) {
      // Cache was cleared
      setCachedRole(null);
    }
  }, [projectId, cacheUpdateTrigger, cachedRole]);
  
  // Update cache when role loads
  useEffect(() => {
    if (!roleLoading && projectRole !== null) {
      const newCacheValue = {
        role: projectRole,
        hasAccess: hasAccess,
        loading: false,
      };
      roleCache.set(projectId, newCacheValue);
      setCachedRole(newCacheValue);
    }
  }, [roleLoading, projectRole, hasAccess, projectId, roleCache]);
  
  // Return cached value if available (even if loading, use optimistic value)
  if (cached) {
    // If we have an optimistic role (like project_manager for creator), use it even while loading
    if (cached.role && cached.loading) {
      return {
        ...buildRoleState(cached.role, cached.hasAccess, false), // Use optimistic value
      };
    }
    
    // If cached and not loading, return it
    if (!cached.loading) {
      return {
        ...buildRoleState(cached.role, cached.hasAccess, false),
      };
    }
    
    // If cached but loading and no optimistic value, return loading state
    return {
      ...buildRoleState(cached.role, cached.hasAccess, true),
    };
  }
  
  // No cache - if the inner hook has a definitive role (e.g. team-based
  // user_role from the project payload), trust it. This prevents the
  // creator-PM optimism from overriding a real moderator assignment for
  // the project's creator.
  if (!roleLoading && projectRole !== null) {
    return {
      ...buildRoleState(projectRole, hasAccess, false),
    };
  }

  // No cache and inner hook still loading - use optimistic rendering for project creator
  if (project && project.created_by === user?.id) {
    return {
      ...buildRoleState('project_manager', true, false), // Don't show loading, use optimistic value
    };
  }

  // No cache, return live data
  return {
    ...buildRoleState(roleLoading ? null : projectRole, roleLoading ? true : hasAccess, roleLoading),
  };
};

// Helper component to conditionally render scheduler tab link based on RBAC
const SchedulerTabLink = ({ 
  projectId,
  project,
  getCurrentTab, 
  getCurrentProjectId, 
  setIsMobileMenuOpen, 
  setProjectHandler,
  roleCache,
  cacheUpdateTrigger
}: {
  projectId: string;
  project: Project;
  getCurrentTab: () => string | null;
  getCurrentProjectId: () => string | null;
  setIsMobileMenuOpen: (open: boolean) => void;
  setProjectHandler: (project: Project) => void;
  roleCache: RoleCache;
  cacheUpdateTrigger: number;
}) => {
  const {loading: roleLoading, isAdmin, isModerator, isScheduler, isProjectManager} = useCachedProjectRole(projectId, roleCache, project, cacheUpdateTrigger);
  
  // Don't render while loading to prevent flickering
  if (roleLoading) {
    return null;
  }
  
  // Show scheduler tab for PM, scheduler, admin, moderator; hide for participants
  if (!isModerator && !isScheduler && !isProjectManager && !isAdmin) {
    return null;
  }
  
  return (
    <Link
      to={`/projects?projectId=${encodeURIComponent(projectId)}&tab=scheduler`}
      onClick={() => {
        setIsMobileMenuOpen(false);
        setProjectHandler(project);
      }}
      className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs ${
        getCurrentTab() === 'scheduler' && getCurrentProjectId() === projectId
          ? 'bg-black text-white dark:bg-white dark:text-black'
          : 'hover:bg-accent hover:text-accent-foreground'
      }`}
    >
      <Calendar className="h-3 w-3" /> Scheduler
    </Link>
  );
};

// Helper component to conditionally render team tab link based on RBAC
const TeamTabLink = ({ 
  projectId,
  project,
  getCurrentTab, 
  getCurrentProjectId, 
  setIsMobileMenuOpen, 
  setProjectHandler,
  roleCache,
  cacheUpdateTrigger
}: {
  projectId: string;
  project: Project;
  getCurrentTab: () => string | null;
  getCurrentProjectId: () => string | null;
  setIsMobileMenuOpen: (open: boolean) => void;
  setProjectHandler: (project: Project) => void;
  roleCache: RoleCache;
  cacheUpdateTrigger: number;
}) => {
  const {loading: roleLoading, isAdmin, isModerator, isScheduler, isProjectManager} = useCachedProjectRole(projectId, roleCache, project, cacheUpdateTrigger);
  
  // Don't render while loading to prevent flickering
  if (roleLoading) {
    return null;
  }
  
  // Show team tab for PM, scheduler, moderator, admin
  if (!isProjectManager && !isScheduler && !isModerator && !isAdmin) {
    return null;
  }
  
  return (
    <Link
      to={`/projects?projectId=${encodeURIComponent(projectId)}&tab=team`}
      onClick={() => {
        setIsMobileMenuOpen(false);
        setProjectHandler(project);
      }}
      className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs ${
        getCurrentTab() === 'team' && getCurrentProjectId() === projectId
          ? 'bg-black text-white dark:bg-white dark:text-black'
          : 'hover:bg-accent hover:text-accent-foreground'
      }`}
    >
      <Users className="h-3 w-3" /> Team
    </Link>
  );
};

// Helper component to conditionally render overview tab link based on RBAC
const OverviewTabLink = ({ 
  projectId,
  project,
  getCurrentTab, 
  getCurrentProjectId, 
  setIsMobileMenuOpen, 
  setProjectHandler,
  location,
  roleCache,
  cacheUpdateTrigger
}: {
  projectId: string;
  project: Project;
  getCurrentTab: () => string | null;
  getCurrentProjectId: () => string | null;
  setIsMobileMenuOpen: (open: boolean) => void;
  setProjectHandler: (project: Project) => void;
  location: any;
  roleCache: RoleCache;
  cacheUpdateTrigger: number;
}) => {
  const {hasAccess, loading: roleLoading, isAdmin, isModerator, isScheduler, isProjectManager} = useCachedProjectRole(projectId, roleCache, project, cacheUpdateTrigger);
  
  // Don't render while loading to prevent flickering
  if (roleLoading) {
    return null;
  }
  
  // Show overview tab for anyone with project access (PM, scheduler, moderator, admin, participants)
  // This ensures schedulers can always see the overview
  if (!hasAccess && !isProjectManager && !isScheduler && !isModerator && !isAdmin) {
    return null;
  }
  
  return (
    <Link
      to={`/projects?projectId=${encodeURIComponent(projectId)}&tab=overview`}
      onClick={() => {
        setIsMobileMenuOpen(false);
        setProjectHandler(project);
      }}
      className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs ${
        (getCurrentTab() === 'overview' && getCurrentProjectId() === projectId) || (!getCurrentTab() && location.pathname === '/projects' && getCurrentProjectId() === projectId)
          ? 'bg-black text-white dark:bg-white dark:text-black'
          : 'hover:bg-accent hover:text-accent-foreground'
      }`}
    >
      <FolderOpen className="h-3 w-3" /> Overview
    </Link>
  );
};

// Helper component to conditionally render action center tab link based on RBAC.
// Positioned directly below Overview and above Team. Visible to PM, scheduler,
// moderator and admin; hidden from participants (internal-only for now).
/** Inventory tab inside a project: what that project holds across its sites. */
const InventoryTabLink = ({
  projectId,
  project,
  getCurrentTab,
  getCurrentProjectId,
  setIsMobileMenuOpen,
  setProjectHandler,
  roleCache,
  cacheUpdateTrigger,
}: {
  projectId: string;
  project: Project;
  getCurrentTab: () => string | null;
  getCurrentProjectId: () => string | null;
  setIsMobileMenuOpen: (open: boolean) => void;
  setProjectHandler: (project: Project) => void;
  roleCache: RoleCache;
  cacheUpdateTrigger: number;
}) => {
  const {loading: roleLoading, isAdmin, isScheduler, isProjectManager} =
    useCachedProjectRole(projectId, roleCache, project, cacheUpdateTrigger);

  if (roleLoading) return null;
  // Same audience as the standalone Inventory page.
  if (!isProjectManager && !isScheduler && !isAdmin) return null;

  return (
    <Link
      to={`/projects?projectId=${encodeURIComponent(projectId)}&tab=inventory`}
      onClick={() => {
        setIsMobileMenuOpen(false);
        setProjectHandler(project);
      }}
      className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs ${
        getCurrentTab() === 'inventory' && getCurrentProjectId() === projectId
          ? 'bg-black text-white dark:bg-white dark:text-black'
          : 'hover:bg-accent hover:text-accent-foreground'
      }`}
    >
      <Package className="h-3 w-3" /> Inventory
    </Link>
  );
};

const ActionCenterTabLink = ({
  projectId,
  project,
  getCurrentTab,
  getCurrentProjectId,
  setIsMobileMenuOpen,
  setProjectHandler,
  roleCache,
  cacheUpdateTrigger,
}: {
  projectId: string;
  project: Project;
  getCurrentTab: () => string | null;
  getCurrentProjectId: () => string | null;
  setIsMobileMenuOpen: (open: boolean) => void;
  setProjectHandler: (project: Project) => void;
  roleCache: RoleCache;
  cacheUpdateTrigger: number;
}) => {
  const {loading: roleLoading, isAdmin, isModerator, isScheduler, isProjectManager} = useCachedProjectRole(projectId, roleCache, project, cacheUpdateTrigger);
  const {user} = useAuth();
  const {state: {currentProject}} = useProjectsData();

  const isActive =
    getCurrentTab() === 'action-center' && getCurrentProjectId() === projectId;
  // The Action Center page derives the Email Setup card from the freshly-loaded
  // currentProject, whereas the sidebar list item is served from a cache and can
  // lag after templates are configured. Reuse currentProject for the project in
  // view so the badge counts the same cards the page shows (no phantom count).
  const badgeProject =
    currentProject?.id === projectId ? (currentProject as Project) : project;
  // Scope the count by the *resolved* project role (the same one the Action
  // Center page uses), not the sidebar's optimistic guess — otherwise a
  // moderator/scheduler who is the project creator (or any Centific user whose
  // role hasn't resolved) is treated as a PM and picks up PM-only cards like
  // Email Setup. Falls back to the cached role flags until the role is known.
  const resolvedFlags = getProjectPermissionFlags({
    projectRole: badgeProject?.user_role ?? null,
    roleLoading: false,
    userRole: user?.role,
    userEmail: user?.email,
  });
  const badgeFlags = badgeProject?.user_role
    ? resolvedFlags
    : {isProjectManager: isProjectManager || isAdmin, isScheduler, isModerator};
  // `recalcSignal` flips as the tab is entered/left, so the count refreshes on
  // navigation into/out of the tab (in addition to on page load / mount).
  const {count} = useActionCenterCount({
    projectId,
    project: badgeProject,
    flags: badgeFlags,
    recalcSignal: isActive,
  });

  // Don't render while loading to prevent flickering
  if (roleLoading) {
    return null;
  }

  // Show action center tab for PM, scheduler, moderator, admin
  if (!isProjectManager && !isScheduler && !isModerator && !isAdmin) {
    return null;
  }

  return (
    <Link
      to={`/projects?projectId=${encodeURIComponent(projectId)}&tab=action-center`}
      onClick={() => {
        setIsMobileMenuOpen(false);
        setProjectHandler(project);
      }}
      className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs ${
        isActive
          ? 'bg-black text-white dark:bg-white dark:text-black'
          : 'hover:bg-accent hover:text-accent-foreground'
      }`}
    >
      <Inbox className="h-3 w-3" /> Action Center
      {count > 0 && (
        <Badge
          variant="secondary"
          data-testid="action-center-nav-badge"
          aria-label={`${count} open items`}
          className="ml-auto h-5 min-w-5 justify-center px-1.5"
        >
          {count}
        </Badge>
      )}
    </Link>
  );
};

// Helper component to conditionally render participants tab link based on RBAC
const ParticipantsTabLink = ({
  projectId,
  project,
  getCurrentTab, 
  getCurrentProjectId, 
  setIsMobileMenuOpen, 
  setProjectHandler,
  location,
  roleCache,
  cacheUpdateTrigger
}: {
  projectId: string;
  project: Project;
  getCurrentTab: () => string | null;
  getCurrentProjectId: () => string | null;
  setIsMobileMenuOpen: (open: boolean) => void;
  setProjectHandler: (project: Project) => void;
  location: ReturnType<typeof useLocation>;
  roleCache: RoleCache;
  cacheUpdateTrigger: number;
}) => {
  const {loading: roleLoading, isAdmin, isModerator, isScheduler, isProjectManager} = useCachedProjectRole(projectId, roleCache, project, cacheUpdateTrigger);
  const {state: {currentProject}} = useProjectsData();

  // Don't render while loading to prevent flickering
  if (roleLoading) {
    return null;
  }
  
  // Show participants tab for PM, scheduler, admin; hide for moderators
  if (isModerator || (!isScheduler && !isProjectManager && !isAdmin)) {
    return null;
  }

  const path = location.pathname;
  const isParticipantsPath = path === '/participants' || path === '/invite-participants';
  const searchParams = new URLSearchParams(location.search);
  const urlProjectId =
    searchParams.get('projectId') || searchParams.get('project_id') || '';
  const contextProjectId = currentProject?.id || '';
  const resolvedProjectId =
    urlProjectId || (isParticipantsPath ? contextProjectId : '');
  const isActive =
    (getCurrentTab() === 'participants' && getCurrentProjectId() === projectId) ||
    (isParticipantsPath && resolvedProjectId === projectId);
  
  return (
    <Link
      to={`/projects?projectId=${encodeURIComponent(projectId)}&tab=participants`}
      onClick={() => {
        setIsMobileMenuOpen(false);
        setProjectHandler(project);
      }}
      className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs ${
        isActive
          ? 'bg-black text-white dark:bg-white dark:text-black'
          : 'hover:bg-accent hover:text-accent-foreground'
      }`}
    >
      <Users className="h-3 w-3" /> Participants
    </Link>
  );
};

// Helper component to conditionally render schedule tab link based on RBAC
const ScheduleTabLink = ({ 
  projectId,
  project,
  setIsMobileMenuOpen, 
  setProjectHandler,
  location,
  roleCache,
  cacheUpdateTrigger
}: {
  projectId: string;
  project: Project;
  setIsMobileMenuOpen: (open: boolean) => void;
  setProjectHandler: (project: Project) => void;
  location: any;
  roleCache: RoleCache;
  cacheUpdateTrigger: number;
}) => {
  const {hasAccess, loading: roleLoading, isAdmin, isModerator, isScheduler, isProjectManager} = useCachedProjectRole(projectId, roleCache, project, cacheUpdateTrigger);
  
  // Don't render while loading to prevent flickering
  if (roleLoading) {
    return null;
  }
  
  // Show schedule tab for PM, scheduler, moderator, admin, and anyone with project access
  if (!isProjectManager && !isScheduler && !isModerator && !isAdmin && !hasAccess) {
    return null;
  }
  
  const scheduleSearch = new URLSearchParams(location.search);
  const bookProjectId = scheduleSearch.get('projectId');
  const isScheduleCalendar =
    location.pathname === '/schedules' &&
    scheduleSearch.get('projectId') === projectId;
  const isBookFromScheduleCalendar =
    location.pathname === '/schedules/book' &&
    scheduleSearch.get('returnToScheduler') !== 'true' &&
    bookProjectId === projectId;

  return (
    <Link
      to={`/schedules?projectId=${encodeURIComponent(projectId)}`}
      onClick={() => {
        setIsMobileMenuOpen(false);
        setProjectHandler(project);
      }}
      className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs ${
        isScheduleCalendar || isBookFromScheduleCalendar
          ? 'bg-black text-white dark:bg-white dark:text-black'
          : 'hover:bg-accent hover:text-accent-foreground'
      }`}
    >
      <CheckSquare className="h-3 w-3" /> Schedule
    </Link>
  );
};

// Helper component to conditionally render tasks tab link based on RBAC
const TasksTabLink = ({ 
  projectId,
  project,
  getCurrentTab, 
  getCurrentProjectId, 
  setIsMobileMenuOpen, 
  setProjectHandler,
  location,
  roleCache,
  cacheUpdateTrigger
}: {
  projectId: string;
  project: Project;
  getCurrentTab: () => string | null;
  getCurrentProjectId: () => string | null;
  setIsMobileMenuOpen: (open: boolean) => void;
  setProjectHandler: (project: Project) => void;
  location: any;
  roleCache: RoleCache;
  cacheUpdateTrigger: number;
}) => {
  const {loading: roleLoading, isAdmin, isModerator, isScheduler} = useCachedProjectRole(projectId, roleCache, project, cacheUpdateTrigger);
  const {state: {currentProject}} = useProjectsData();
  // The sidebar item comes from a cached list that can lag a just-saved
  // setting, so prefer the freshly-loaded project when it is the one in view.
  const accessProject =
    currentProject?.id === projectId ? (currentProject as Project) : project;

  // Don't render while loading to prevent flickering
  if (roleLoading) {
    return null;
  }
  
  // Hide tasks tab for moderators and schedulers; admin sees it
  if ((isModerator || isScheduler) && !isAdmin) {
    return null;
  }

  // A moderator on a project that restricts them to their own sessions loses
  // tasks even when they are a platform admin: this gate follows the project
  // role, the same way the Participants tab already does.
  if (isModeratorSessionAccessRestricted(isModerator, accessProject)) {
    return null;
  }
  
  // Check if tasks tab is active — /projects?tab=tasks, /tasks, /tasks/add, /tasks/:id/edit, etc.
  const searchParams = new URLSearchParams(location.search);
  const urlProjectId = searchParams.get('projectId') || searchParams.get('project_id');
  const isTasksPath =
    location.pathname === '/tasks' || location.pathname.startsWith('/tasks/');
  const isActive =
    (getCurrentTab() === 'tasks' && getCurrentProjectId() === projectId) ||
    (isTasksPath && urlProjectId === projectId);
  
  return (
    <Link
      to={`/tasks?project_id=${encodeURIComponent(projectId)}`}
      onClick={() => {
        setIsMobileMenuOpen(false);
        setProjectHandler(project);
      }}
      className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs ${
        isActive
          ? 'bg-black text-white dark:bg-white dark:text-black'
          : 'hover:bg-accent hover:text-accent-foreground'
      }`}
    >
      <CheckSquare className="h-3 w-3" /> Tasks
    </Link>
  );
};

// Helper component to conditionally render submit data tab link based on RBAC
const SubmitDataTabLink = ({ 
  projectId,
  project,
  getCurrentTab, 
  getCurrentProjectId, 
  setIsMobileMenuOpen, 
  setProjectHandler,
  roleCache,
  cacheUpdateTrigger
}: {
  projectId: string;
  project: Project;
  getCurrentTab: () => string | null;
  getCurrentProjectId: () => string | null;
  setIsMobileMenuOpen: (open: boolean) => void;
  setProjectHandler: (project: Project) => void;
  roleCache: RoleCache;
  cacheUpdateTrigger: number;
}) => {
  const {loading: roleLoading, isAdmin, isScheduler} = useCachedProjectRole(projectId, roleCache, project, cacheUpdateTrigger);
  
  // Don't render while loading to prevent flickering
  if (roleLoading) {
    return null;
  }
  
  // Hide submit data tab for schedulers only; admin and moderators see it
  if (isScheduler && !isAdmin) {
    return null;
  }
  
  return (
    <Link
      to={`/projects?projectId=${encodeURIComponent(projectId)}&tab=submit-data`}
      onClick={() => {
        setIsMobileMenuOpen(false);
        setProjectHandler(project);
      }}
      className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs ${
        getCurrentTab() === 'submit-data' && getCurrentProjectId() === projectId
          ? 'bg-black text-white dark:bg-white dark:text-black'
          : 'hover:bg-accent hover:text-accent-foreground'
      }`}
    >
      <Database className="h-3 w-3" /> Submit Data
    </Link>
  );
};

// Helper component to conditionally render payment approvals tab link based on RBAC
const PaymentApprovalsTabLink = ({ 
  projectId,
  project,
  getCurrentTab, 
  getCurrentProjectId, 
  setIsMobileMenuOpen, 
  setProjectHandler,
  roleCache,
  cacheUpdateTrigger
}: {
  projectId: string;
  project: Project;
  getCurrentTab: () => string | null;
  getCurrentProjectId: () => string | null;
  setIsMobileMenuOpen: (open: boolean) => void;
  setProjectHandler: (project: Project) => void;
  roleCache: RoleCache;
  cacheUpdateTrigger: number;
}) => {
  const {loading: roleLoading, isAdmin, isProjectManager, isModerator} = useCachedProjectRole(projectId, roleCache, project, cacheUpdateTrigger);

  if (!isPaymentApprovalsEnabled()) {
    return null;
  }

  // Don't render while loading to prevent flickering
  if (roleLoading) {
    return null;
  }

  // Show for PM, moderator, admin
  if (!isProjectManager && !isModerator && !isAdmin) {
    return null;
  }

  return (
    <Link
      to={`/projects?projectId=${encodeURIComponent(projectId)}&tab=payment-approvals`}
      onClick={() => {
        setIsMobileMenuOpen(false);
        setProjectHandler(project);
      }}
      className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs ${
        getCurrentTab() === 'payment-approvals' && getCurrentProjectId() === projectId
          ? 'bg-black text-white dark:bg-white dark:text-black'
          : 'hover:bg-accent hover:text-accent-foreground'
      }`}
    >
      <DollarSign className="h-3 w-3" /> Payment Approvals
    </Link>
  );
};

// Helper component for the Project Setup tab link. Project Setup hosts the
// setup assistant, Email Setup, Bonus & Partial Payment, SMS Setup and Project
// Modules as tabs, so it is shown to the union of those tabs' audiences
// (PM / scheduler / admin) and each tab gates itself inside the page.
const ProjectSetupTabLink = ({
  projectId,
  project,
  getCurrentTab,
  getCurrentProjectId,
  setIsMobileMenuOpen,
  setProjectHandler,
  roleCache,
  cacheUpdateTrigger,
}: {
  projectId: string;
  project: Project;
  getCurrentTab: () => string | null;
  getCurrentProjectId: () => string | null;
  setIsMobileMenuOpen: (open: boolean) => void;
  setProjectHandler: (project: Project) => void;
  roleCache: RoleCache;
  cacheUpdateTrigger: number;
}) => {
  const {loading: roleLoading, isAdmin, isProjectManager, isScheduler} = useCachedProjectRole(
    projectId,
    roleCache,
    project,
    cacheUpdateTrigger
  );

  if (roleLoading) {
    return null;
  }
  if (!isAdmin && !isProjectManager && !isScheduler) {
    return null;
  }

  // The legacy tab values still resolve to this page, so keep it highlighted.
  const currentTab = (getCurrentTab() || '').toLowerCase();
  const isActive =
    ['project-setup', 'agent-setup', 'email-setup', 'project-configuration'].includes(
      currentTab
    ) && getCurrentProjectId() === projectId;

  return (
    <Link
      to={`/projects?projectId=${encodeURIComponent(projectId)}&tab=project-setup`}
      onClick={() => {
        setIsMobileMenuOpen(false);
        setProjectHandler(project);
      }}
      className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs ${
        isActive
          ? 'bg-black text-white dark:bg-white dark:text-black'
          : 'hover:bg-accent hover:text-accent-foreground'
      }`}
    >
      <Settings className="h-3 w-3" /> Project Setup
    </Link>
  );
};

// Helper component to conditionally render data uploads tab link based on RBAC
const DataUploadsTabLink = ({ 
  projectId,
  project,
  getCurrentTab, 
  getCurrentProjectId, 
  setIsMobileMenuOpen, 
  setProjectHandler,
  roleCache,
  cacheUpdateTrigger
}: {
  projectId: string;
  project: Project;
  getCurrentTab: () => string | null;
  getCurrentProjectId: () => string | null;
  setIsMobileMenuOpen: (open: boolean) => void;
  setProjectHandler: (project: Project) => void;
  roleCache: RoleCache;
  cacheUpdateTrigger: number;
}) => {
  const {loading: roleLoading, isScheduler} = useCachedProjectRole(projectId, roleCache, project, cacheUpdateTrigger);
  
  // Don't render while loading to prevent flickering
  if (roleLoading) {
    return null;
  }
  
  // Hide data uploads tab for schedulers only, allow moderators and PMs to see it
  if (isScheduler) {
    return null;
  }
  
  return (
    <Link
      to={`/projects?projectId=${encodeURIComponent(projectId)}&tab=data-uploads`}
      onClick={() => {
        setIsMobileMenuOpen(false);
        setProjectHandler(project);
      }}
      className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs ${
        getCurrentTab() === 'data-uploads' && getCurrentProjectId() === projectId
          ? 'bg-black text-white dark:bg-white dark:text-black'
          : 'hover:bg-accent hover:text-accent-foreground'
      }`}
    >
      <Upload className="h-3 w-3" /> Data Uploads
    </Link>
  );
};

// Helper component to conditionally render project settings tab link based on RBAC
const ProjectSettingsTabLink = (_props: {
  projectId: string;
  project: Project;
  getCurrentTab: () => string | null;
  getCurrentProjectId: () => string | null;
  setIsMobileMenuOpen: (open: boolean) => void;
  setProjectHandler: (project: Project) => void;
  roleCache: RoleCache;
  cacheUpdateTrigger: number;
}) => null;

// Define role-based navigation items
const ROLE_NAVIGATION = {
  project_manager: [
    {path: '/', icon: Home, label: 'Dashboard'},
    {path: '/projects', icon: FolderOpen, label: 'Projects'},
    {path: '/tasks', icon: CheckSquare, label: 'Tasks'},
    {path: '/moderators', icon: UserCheck, label: 'Moderators'},
    {
      path: '/moderator-assignments',
      icon: Users,
      label: 'Moderator Assignments',
    },
    {path: '/participants', icon: Users, label: 'Participants'},
    {path: '/survey-upload', icon: Upload, label: 'Survey Upload'},
    {path: '/journal-entry', icon: BookOpen, label: 'Journal Entry'},
    {path: '/multimedia-files', icon: Upload, label: 'Multimedia Files'},
    {path: '/schedules', icon: Calendar, label: 'Schedules'},
    {path: '/surveys', icon: FileText, label: 'Surveys'},
    {path: '/role-management', icon: Shield, label: 'Role Management'},
    // Hidden per user request: {path: '/feedback', icon: MessageSquare, label: 'Feedback'},
    // Hidden per user request: {path: '/analytics', icon: BarChart3, label: 'Analytics'},
  ],
  moderator: [
    {path: '/', icon: Home, label: 'Dashboard'},
    {path: '/tasks', icon: CheckSquare, label: 'Tasks'},
    {path: '/participants', icon: Users, label: 'Participants'},
    {path: '/journal-entry', icon: BookOpen, label: 'Journal Entry'},
    {path: '/multimedia-files', icon: Upload, label: 'Multimedia Files'},
    {path: '/schedules', icon: Calendar, label: 'Schedules'},
    {path: '/moderator-assignments', icon: Users, label: 'My Assignments'},
    {path: '/moderator', icon: UserCheck, label: 'Moderator Dashboard'},
    {path: '/moderator/tasks', icon: CheckSquare, label: 'My Tasks'},
    {path: '/moderator/availability', icon: Calendar, label: 'My Availability'},
    {path: '/moderator/participants', icon: Users, label: 'My Participants'},
    {path: '/moderator/schedules', icon: Calendar, label: 'My Schedules'},
  ],
  participant: [
    {path: '/', icon: Home, label: 'Dashboard'},
    {path: '/participant', icon: Users, label: 'Participant Portal'},
    {path: '/participant/project/schedule', icon: Calendar, label: 'Schedule'},
  ],
  scheduler: [
    {path: '/', icon: Home, label: 'Dashboard'},
    {path: '/projects', icon: FolderOpen, label: 'Projects'},
    {path: '/tasks', icon: CheckSquare, label: 'Tasks'},
    {path: '/moderators', icon: UserCheck, label: 'Moderators'},
    {path: '/moderator-assignments', icon: Users, label: 'Moderator Assignments'},
    {path: '/participants', icon: Users, label: 'Participants'},
    {path: '/survey-upload', icon: Upload, label: 'Survey Upload'},
    {path: '/journal-entry', icon: BookOpen, label: 'Journal Entry'},
    {path: '/multimedia-files', icon: Upload, label: 'Multimedia Files'},
    {path: '/schedules', icon: Calendar, label: 'Schedules'},
    {path: '/role-management', icon: Shield, label: 'Role Management'},
  ],
  admin: [
    {path: '/', icon: Home, label: 'Dashboard'},
    {path: '/projects', icon: FolderOpen, label: 'Projects'},
    {path: '/tasks', icon: CheckSquare, label: 'Tasks'},
    {path: '/moderators', icon: UserCheck, label: 'Moderators'},
    {path: '/moderator-assignments', icon: Users, label: 'Moderator Assignments'},
    {path: '/participants', icon: Users, label: 'Participants'},
    {path: '/survey-upload', icon: Upload, label: 'Survey Upload'},
    {path: '/journal-entry', icon: BookOpen, label: 'Journal Entry'},
    {path: '/multimedia-files', icon: Upload, label: 'Multimedia Files'},
    {path: '/schedules', icon: Calendar, label: 'Schedules'},
    {path: '/role-management', icon: Shield, label: 'Role Management'},
  ],
} as const;

const VALID_USER_ROLES = new Set(['admin', 'project_manager', 'moderator', 'scheduler', 'participant']);

// Resolve a role for a Centific/non-Centific email (moderator check for Centific users).
const resolveRoleFromEmail = async (email: string): Promise<UserRole> => {
  if (!email.includes('@centific.com')) {
    return 'participant';
  }
  // Check if user is a moderator using backend API; default to PM for Centific users.
  try {
    await schedulerApi.getModeratorByEmail(email);
    return 'moderator';
  } catch (error) {
    console.warn('[Navigation] Failed to check moderator status:', error);
    return 'project_manager';
  }
};

// Determine the effective user role from the backend role, with email-based fallback.
const determineUserRole = async (user: any): Promise<UserRole | null> => {
  const rawRole = (user?.role || '').toLowerCase();
  let role: UserRole | null = rawRole === 'admin' ? 'admin' : (user?.role as UserRole);

  // Fallback logic if backend API didn't provide a role
  if (!role) {
    const email = user?.email;
    role = email ? await resolveRoleFromEmail(email) : null;
  }

  // Admin is valid; scheduler uses PM nav. Unknown roles collapse to PM/participant.
  if (role && !VALID_USER_ROLES.has(role)) {
    role = role === 'project_manager' ? 'project_manager' : 'participant';
  }
  return role;
};

// Friendly display name helper
const computeDisplayName = (u: any): string => {
  const md = u?.user_metadata || {};
  const fromMeta = md.full_name || md.name || md.display_name || '';
  if (fromMeta && typeof fromMeta === 'string') return fromMeta;
  const email: string = u?.email || '';
  if (!email) return '';
  const local = email.split('@')[0] || '';
  // Replace separators with spaces and capitalize each word
  return local
    .split(/[._-]+/)
    .filter(Boolean)
    .map((part: string) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

/**
 * The flat role menus are project-agnostic, but the task list is project-scoped
 * and renders a "pick a project" notice without one. Carry the selected project
 * so the entry opens its tasks.
 */
const navItemTarget = (path: string, projectId?: string | null) =>
  path === '/tasks' && projectId
    ? `/tasks?project_id=${encodeURIComponent(projectId)}`
    : path;

/**
 * The project the sidebar should treat as active. Pages such as Schedule carry
 * their project in the URL without ever writing it to the projects context, so
 * the URL is consulted too — otherwise project-scoped entries in the flat role
 * menus have nothing to point at or gate on.
 */
const resolveNavProject = (
  currentProject: Project | null | undefined,
  projects: Project[],
  search: string,
) => {
  const params = new URLSearchParams(search);
  const navProjectId =
    currentProject?.id ||
    params.get('projectId') ||
    params.get('project_id') ||
    '';
  if (!navProjectId) return {navProjectId: '', navProject: null};
  const navProject =
    currentProject?.id === navProjectId
      ? currentProject
      : (projects.find((project) => project.id === navProjectId) ?? null);
  return {navProjectId, navProject};
};

// Derive role-driven navigation flags/items. Scheduler uses PM-style nav.
const getNavRoleInfo = (
  isAdmin: boolean,
  effectiveRole: string | null | undefined,
  userRole: UserRole | null,
  activeProject?: Project | null,
  projects: Project[] = [],
) => {
  const displayRole = isAdmin ? (effectiveRole ?? 'admin') : userRole;
  // Moderators load the list too — not to list projects in the sidebar (that
  // section stays PM/admin/scheduler only) but because their own project
  // settings decide which project-scoped entries they may see.
  const canLoadProjects =
    displayRole === 'project_manager' ||
    displayRole === 'admin' ||
    displayRole === 'scheduler' ||
    displayRole === 'moderator';
  const navRole = displayRole === 'scheduler' ? 'project_manager' : displayRole;
  const showProjectsSection = ['project_manager', 'admin', 'scheduler'].includes(displayRole ?? '');
  const roleItems = navRole
    ? (ROLE_NAVIGATION[navRole as keyof typeof ROLE_NAVIGATION] ?? [])
    : [];
  const navigationItems = withoutModeratorTaskNav(
    roleItems,
    isModeratorTaskNavRestricted(
      displayRole === 'moderator',
      activeProject,
      projects,
    ),
  );
  return { displayRole, canLoadProjects, navRole, showProjectsSection, navigationItems };
};

// Sidebar header: profile dropdown (PM/Admin) or plain title, plus the mobile close button.
const NavigationHeader = ({
  user,
  userRole,
  effectiveRole,
  profileMenuOpen,
  setProfileMenuOpen,
  setViewAsRole,
  navigate,
  setIsMobileMenuOpen,
  isMobileMenuOpen,
  location,
  onSignOut,
}: {
  user: any;
  userRole: UserRole | null;
  effectiveRole: string | null | undefined;
  profileMenuOpen: boolean;
  setProfileMenuOpen: (open: boolean) => void;
  setViewAsRole: (role: 'project_manager' | 'admin') => void;
  navigate: ReturnType<typeof useNavigate>;
  setIsMobileMenuOpen: (open: boolean) => void;
  isMobileMenuOpen: boolean;
  location: ReturnType<typeof useLocation>;
  onSignOut: () => void;
}) => {
  return (
    <div className="p-4 border-b border-border relative">
      {(userRole === 'project_manager' || userRole === 'admin') ? (
        // PM header: avatar + name + email + chevron
        <DropdownMenu open={profileMenuOpen} onOpenChange={setProfileMenuOpen}>
          <DropdownMenuTrigger asChild>
            <button type="button" className="w-full text-left rounded-xl p-3 flex items-center justify-between hover:bg-accent group">
              <div className="flex items-center gap-3">
                {(() => {
                  const displayName = computeDisplayName(user as any);
                  const initials =
                    displayName
                      .split(/\s+/)
                      .filter(Boolean)
                      .map((s) => s[0]?.toUpperCase())
                      .slice(0, 2)
                      .join('') || 'U';
                  return (
                    <div className="h-9 w-9 rounded-full bg-muted text-foreground/80 flex items-center justify-center font-semibold">
                      {initials}
                    </div>
                  );
                })()}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate flex items-center gap-1">
                    {computeDisplayName(user as any)}
                      <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {user?.email}
                  </div>
                </div>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <div className="px-3 py-2">
              <div className="text-sm font-medium truncate">
                {computeDisplayName(user as any)}
              </div>
            </div>
            {userRole === 'admin' && (
              <>
                <DropdownMenuSeparator />
                <div className="px-3 py-2">
                  <div className="text-xs text-muted-foreground mb-1">View as</div>
                  <div className="flex flex-wrap gap-1">
                    {(['project_manager', 'admin'] as const).map((r) => (
                      <button
                        key={r}
                        type="button"
                        onClick={() => {
                          setViewAsRole(r);
                          setProfileMenuOpen(false);
                        }}
                        className={cn(
                          'text-xs px-2 py-1 rounded hover:bg-accent',
                          (effectiveRole ?? 'admin') === r && 'bg-accent font-medium'
                        )}
                      >
                        {r === 'project_manager' ? 'PM' : 'Admin'}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                navigate('/account-settings');
                setIsMobileMenuOpen(false);
              }}
              className={`gap-2 ${
                location.pathname === '/account-settings'
                  ? '!bg-black !text-white dark:!bg-white dark:!text-black hover:!bg-black hover:!text-white dark:hover:!bg-white dark:hover:!text-black'
                  : ''
              }`}
            >
              <Settings className="h-4 w-4" /> Account Settings
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                navigate('/profile');
                setIsMobileMenuOpen(false);
              }}
              className={`gap-2 ${
                location.pathname === '/profile'
                  ? '!bg-black !text-white dark:!bg-white dark:!text-black hover:!bg-black hover:!text-white dark:hover:!bg-white dark:hover:!text-black'
                  : ''
              }`}
            >
              <User className="h-4 w-4" /> My Profile
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                onSignOut();
              }}
              className="gap-2"
            >
              <LogOut className="h-4 w-4" /> Log Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <>
          <h1 className="text-xl font-bold text-foreground">
            OneData Platform
          </h1>
          {user?.email && (
            <p className="text-sm text-muted-foreground">{user.email}</p>
          )}
          {userRole && (
            <p className="text-xs text-muted-foreground capitalize mt-1">
              Role: {userRole.replace('_', ' ')}
            </p>
          )}
        </>
      )}
      {isMobileMenuOpen && (
        <button
          type="button"
          onClick={() => setIsMobileMenuOpen(false)}
          className="p-2 rounded-lg lg:hidden hover:bg-accent absolute right-1 top-1"
        >
          <X className="h-5 w-5" />
        </button>
      )}
    </div>
  );
};

const Navigation = ({
  isMobileMenuOpen,
  setIsMobileMenuOpen,
  collapsed = false,
  onToggleCollapse,
}: {
  isMobileMenuOpen: boolean;
  setIsMobileMenuOpen: (open: boolean) => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}) => {
  const queryClient = useQueryClient();
  const {
    setCurrentProject: setCurrentProjectContextApi,
    setProjects: setProjectsContextApi,
    refreshCurrentProject,
  } = useProjectsApi();
  const {state: projectContextState} = useProjectsData();
  const location = useLocation();
  const {user, signOut, isAdmin, effectiveRole, setViewAsRole} = useAuth();
  const canSeeMasterInventory = canAccessMasterInventory({
    effectiveRole,
    hasProjectManagerMembership: user?.hasProjectManagerMembership,
  });
  const {toast} = useToast();
  const navigate = useNavigate();
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [loading, setLoading] = useState(true);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [isRoleSwitching, setIsRoleSwitching] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  // For Admin: use effectiveRole (view-as). Others use userRole. Scheduler uses PM-style nav.
  const {navProjectId, navProject} = resolveNavProject(
    projectContextState.currentProject,
    projectContextState.projects,
    location.search,
  );
  const {displayRole, canLoadProjects, showProjectsSection, navigationItems} =
    getNavRoleInfo(
      isAdmin,
      effectiveRole,
      userRole,
      navProject,
      projectContextState.projects,
    );
  const previousDisplayRoleRef = useRef<string | null>(null);
  // Use context projects instead of local state
  const projects = projectContextState.projects;

  const projectsListQuery = useProjectsListQuery({
    enabled: !!user && canLoadProjects,
  });

  // Role cache for instant tab display
  const [roleCache] = useState<RoleCache>(() => new Map());
  // Project ids with a roles/batch (or fallback per-project) request
  // currently in flight — `roleCache`'s `loading: true` flag alone can't
  // signal this, since it's set optimistically before the request even
  // starts and doesn't flip to false until the request settles. Without
  // this, two overlapping runs of the pre-fetch effect below both see
  // `loading: true` and both fire an identical duplicate request.
  const inFlightRoleFetchesRef = useRef<Set<string>>(new Set());
  const [cacheUpdateTrigger, setCacheUpdateTrigger] = useState(0);
  const [projectsOpen, setProjectsOpen] = useState(() => {
    // Restore projects section state from localStorage on component mount
    if (typeof window !== 'undefined') {
      const savedProjectsOpen = localStorage.getItem('sidebar_projects_open');
      return savedProjectsOpen === 'true';
    }
    return false;
  });
  const [projectSearch, setProjectSearch] = useState('');
  const [expandedProjectId, setExpandedProjectId] = useState<string | null>(() => {
    // Restore expanded state from localStorage on component mount
    if (typeof window !== 'undefined') {
      const savedExpandedId = localStorage.getItem('sidebar_expanded_project_id');
      return savedExpandedId || null;
    }
    return null;
  });

  // Ref to the currently-expanded project row so we can scroll it into view on
  // load/refresh — the sidebar resets to the top otherwise and a deep-in-list
  // project becomes hard to find.
  const expandedProjectRef = useRef<HTMLDivElement | null>(null);
  const didInitialExpandScrollRef = useRef(false);

  // Wrapper function to handle projects section state and localStorage persistence
  const handleProjectsOpenChange = (isOpen: boolean) => {
    setProjectsOpen(isOpen);
    if (typeof window !== 'undefined') {
      localStorage.setItem('sidebar_projects_open', isOpen.toString());
    }
  };

  // Wrapper function to handle both state update and localStorage persistence
  const handleExpandedProjectChange = (projectId: string | null) => {
    setExpandedProjectId(projectId);
    if (typeof window !== 'undefined') {
      if (projectId) {
        localStorage.setItem('sidebar_expanded_project_id', projectId);
      } else {
        localStorage.removeItem('sidebar_expanded_project_id');
      }
    }
  };

  // Bump the trigger so cached-role consumers re-render. Hoisted to component
  // scope so the inline `prev => prev + 1` updater isn't nested inside the
  // role-fetch .then/.catch/forEach callbacks (keeps nesting under 4 levels).
  const bumpCacheUpdateTrigger = () => setCacheUpdateTrigger((prev) => prev + 1);

  // Effect to restore expanded state when projects are loaded
  useEffect(() => {
    if (projects.length > 0 && expandedProjectId) {
      // Verify that the saved project ID still exists in the projects list
      const projectExists = projects.some(p => p.id === expandedProjectId);
      if (!projectExists) {
        handleExpandedProjectChange(null);
      } else {
      }
    }
  }, [projects, expandedProjectId]);

  // When URL has projectId (e.g. from reminder "View Details" or direct link), expand that project and set it as current
  useEffect(() => {
    const urlProjectId = new URLSearchParams(location.search).get('projectId');
    if (!urlProjectId || projects.length === 0) return;
    const project = projects.find((p: Project) => p.id === urlProjectId);
    if (!project) return;
    setExpandedProjectId((current) => (current === urlProjectId ? current : urlProjectId));
    if (typeof window !== 'undefined') {
      localStorage.setItem('sidebar_expanded_project_id', urlProjectId);
    }
    if (projectContextState.currentProject?.id !== urlProjectId) {
      setCurrentProjectContextApi(project);
    }
  }, [location.search, projects, projectContextState.currentProject?.id]);

  // On the first render after projects load, bring the expanded (open) project
  // into view so a refresh doesn't leave the user scrolled to the top of a long
  // project list. Runs once per mount (refresh), not on every manual expand.
  useEffect(() => {
    if (didInitialExpandScrollRef.current) return;
    if (projectsLoading || !projectsOpen || !expandedProjectId) return;
    if (projects.length === 0) return;
    const frame = globalThis.requestAnimationFrame(() => {
      const el = expandedProjectRef.current;
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({block: 'center'});
        didInitialExpandScrollRef.current = true;
      }
    });
    return () => globalThis.cancelAnimationFrame(frame);
  }, [projectsLoading, projectsOpen, expandedProjectId, projects.length]);

  useEffect(() => {
    const checkUserRole = async () => {
      try {
        if (!user) {
          setLoading(false);
          return;
        }
        setUserRole(await determineUserRole(user));
        setLoading(false);
      } catch (error) {
        console.error('[Navigation] Error checking user role:', error);
        setLoading(false);
      }
    };

    checkUserRole();
  }, [user]);

  // When admin switches "View as", invalidate project caches and force a fresh fetch.
  useEffect(() => {
    if (!displayRole) return;

    const previousDisplayRole = previousDisplayRoleRef.current;
    if (previousDisplayRole && previousDisplayRole !== displayRole) {
      roleCache.clear();
      clearProjectRoleCache();
      // Reference-data lists (projects/moderators/session types) are
      // role/project-scoped, so the "View as" switch needs the same
      // localStorage cache bust sign-in/out already does — otherwise the
      // invalidateQueries below just re-serves the previous role's stale
      // cached project list instead of hitting the network.
      clearAllCached();
      bumpCacheUpdateTrigger();
      setIsRoleSwitching(true);
      setProjectsLoading(true);
      setProjectsContextApi([]);
      setCurrentProjectContextApi(null);
      handleExpandedProjectChange(null);
      void queryClient.invalidateQueries({queryKey: queryKeys.projects.list()});
    }

    previousDisplayRoleRef.current = displayRole;
  }, [displayRole, roleCache, setProjectsContextApi, queryClient]);

  // Pre-fetch roles for all projects when projects are loaded (using batch endpoint)
  useEffect(() => {
    const preFetchRoles = async () => {
      if (!user || projects.length === 0) return;
      
      // Filter projects that need role fetching (not cached or cached but
      // loading) AND aren't already being fetched by an overlapping run of
      // this same effect.
      const projectsToFetch = projects.filter(project => {
        const cached = roleCache.get(project.id);
        const alreadyInFlight = inFlightRoleFetchesRef.current.has(project.id);
        return (!cached || cached.loading) && !alreadyInFlight;
      });

      if (projectsToFetch.length === 0) return;

      // Use optimistic rendering: if project was created by current user, assume project_manager
      projectsToFetch.forEach(project => {
        inFlightRoleFetchesRef.current.add(project.id);
        if (!roleCache.has(project.id)) {
          // Optimistic: if user created the project, they're project_manager
          const isCreator = project.created_by === user.id;
          roleCache.set(project.id, {
            role: isCreator ? 'project_manager' : null,
            hasAccess: true, // Optimistically assume access
            loading: true, // Still loading actual role
          });
        }
      });

      const projectIds = projectsToFetch.map(p => p.id);
      try {
        // Use batch endpoint for efficiency
        const response = await projectsApi.getProjectRolesBatch(projectIds);

        if (response?.data) {
          // Update cache with batch results
          Object.entries(response.data).forEach(([projectId, roleInfo]) => {
            roleCache.set(projectId, {
              role: roleInfo.role,
              hasAccess: roleInfo.hasAccess ?? true,
              loading: false,
            });
          });
          // Trigger re-render
          bumpCacheUpdateTrigger();
        }
        projectIds.forEach(id => inFlightRoleFetchesRef.current.delete(id));
      } catch (error) {
        console.warn('[Navigation] Failed to batch pre-fetch roles:', error);
        // Limited fallback (max 15) to avoid hundreds of /role requests
        const fallbackSlice = projectsToFetch.slice(0, 15);
        const fallbackIds = new Set(fallbackSlice.map(p => p.id));
        // Projects beyond the fallback slice get no further attempt this
        // round — release them now so a later effect run can retry.
        projectIds.forEach(id => {
          if (!fallbackIds.has(id)) inFlightRoleFetchesRef.current.delete(id);
        });
        fallbackSlice.forEach(async (project) => {
          try {
            const response = await projectsApi.getProjectRole(project.id);
            if (response?.data) {
              roleCache.set(project.id, {
                role: response.data.role,
                hasAccess: response.data.hasAccess ?? true,
                loading: false,
              });
              bumpCacheUpdateTrigger();
            }
          } catch (err) {
            console.warn(`[Navigation] Failed to fetch role for project ${project.id}:`, err);
          } finally {
            const cached = roleCache.get(project.id);
            if (cached?.loading) {
              roleCache.set(project.id, { ...cached, loading: false });
              bumpCacheUpdateTrigger();
            }
            inFlightRoleFetchesRef.current.delete(project.id);
          }
        });
      }
    };
    
    preFetchRoles();
  }, [projects, user, roleCache]);

  useEffect(() => {
    if (!canLoadProjects) {
      setProjectsLoading(false);
      // A role switch can transiently land here (e.g. before displayRole's
      // dependent state has caught up) — without this, isRoleSwitching would
      // never get cleared and the full-screen switch overlay would hang
      // forever regardless of which page the user is on.
      setIsRoleSwitching(false);
      return;
    }
    if (projectsListQuery.isFetching) {
      setProjectsLoading(true);
    } else {
      setProjectsLoading(false);
      setIsRoleSwitching(false);
    }
  }, [canLoadProjects, projectsListQuery.isFetching]);

  // TanStack Query dedupes /projects/list; sync cache into sidebar context.
  useEffect(() => {
    if (!canLoadProjects) return;
    if (projectsListQuery.data === undefined) return;

    const items = projectsListQuery.data;
    setProjectsContextApi(items);

    if (isRoleSwitching) {
      const currentProjectId = new URLSearchParams(location.search).get('projectId');
      if (currentProjectId && !items.some((p: any) => p?.id === currentProjectId)) {
        navigate('/', {replace: true});
      } else if (currentProjectId) {
        const currentProject = items.find((p: any) => p?.id === currentProjectId);
        if (currentProject) {
          setCurrentProjectContextApi(currentProject);
        }
      }
    }
  }, [
    canLoadProjects,
    projectsListQuery.data,
    isRoleSwitching,
    location.search,
    navigate,
    setProjectsContextApi,
    setCurrentProjectContextApi,
  ]);

  const filteredProjects = useMemo(() => {
    const q = projectSearch.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter((p) =>
      [p?.name, p?.description, p?.status, p?.dynamics_id]
        .filter(Boolean)
        .some((v: string) => String(v).toLowerCase().includes(q))
    );
  }, [projectSearch, projects]);

  const handleSignOut = async () => {
    const {error} = await signOut();

    if (error) {
      toast({
        title: 'Sign Out Failed',
        description: error,
        variant: 'destructive',
      });
    } else {
      toast({
        title: 'Signed Out',
        description: 'You have been successfully signed out.',
      });
      // Clear sidebar state on logout
      if (typeof window !== 'undefined') {
        localStorage.removeItem('sidebar_expanded_project_id');
        localStorage.removeItem('sidebar_projects_open');
      }
      // Ensure we leave protected routes even for SSO-only sessions
      navigate('/auth', {replace: true});
    }
  };

  const setProjectHandler = (project: Project) => {
    const currentProjectId = projectContextState.currentProject?.id;

    // Only touch context when actually switching projects — tab clicks within
    // the same project need neither a context replace nor a background
    // refetch; status/role reconciliation for a project already happens at
    // the point it changes (see ProjectOverview's activate/complete flows).
    if (currentProjectId !== project.id) {
      setCurrentProjectContextApi(project);
      void refreshCurrentProject(project.id);
    }
  };

  // Helper function to get current tab from URL
  const getCurrentTab = () => {
    const searchParams = new URLSearchParams(location.search);
    const tab = searchParams.get('tab');
    if (tab) return tab;

    // Book page lives under /schedules/book but is opened from Scheduler or Schedule;
    // map it so the project sidebar shows the correct sub-item as selected.
    if (location.pathname === '/schedules/book') {
      if (searchParams.get('returnToScheduler') === 'true') {
        return 'scheduler';
      }
      return 'schedule';
    }

    return '';
  };

  // Helper function to get current projectId from URL
  const getCurrentProjectId = () => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('projectId') || '';
  };

  // Collapsed icon-rail mode (lg only; the mobile drawer always shows the full
  // nav). Project dots stay visible so projects can be switched while collapsed.
  const showRail = collapsed && !isMobileMenuOpen;

  // "bg-card border-r border-border h-screen w-64 fixed left-0 top-0 flex flex-col"
  if (loading) {
    return (
      <nav
        className={cn(
          'bg-card border-r border-border h-screen w-64 fixed left-0 top-0 flex flex-col',
          'lg:translate-x-0',
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        <div className="p-6 border-b border-border">
          <h1 className="text-xl font-bold text-foreground">
            OneData Platform
          </h1>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <LoaderSpinner />
        </div>
      </nav>
    );
  }

  return (
    <>
      {isRoleSwitching && (
        <div className="fixed inset-0 z-[120] bg-background/70 flex items-center justify-center">
          <LoaderSpinner />
        </div>
      )}

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <button
          type="button"
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          aria-label="Close menu"
          onClick={() => setIsMobileMenuOpen(false)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setIsMobileMenuOpen(false);
            }
          }}
        />
      )}

      <nav
        className={cn(
          'bg-card border-r border-border h-screen flex flex-col transition-all duration-300 ease-in-out',
          'fixed left-0 top-0 z-50',
          showRail ? 'w-[60px]' : 'w-72',
          'lg:translate-x-0',
          isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {showRail ? (
          <CollapsedRail
            onToggleCollapse={onToggleCollapse}
            setIsMobileMenuOpen={setIsMobileMenuOpen}
            location={location}
            showProjectsSection={showProjectsSection}
            projects={projects}
            getCurrentProjectId={getCurrentProjectId}
            navigate={navigate}
            setProjectHandler={setProjectHandler}
          />
        ) : (
          <>
        {/* Collapse toggle (lg only) */}
        {onToggleCollapse && (
          <button
            type="button"
            onClick={() => onToggleCollapse()}
            title="Collapse sidebar"
            aria-label="Collapse sidebar"
            className="absolute right-2 top-3 z-10 hidden size-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground lg:flex"
          >
            <PanelLeftClose className="h-4 w-4" />
          </button>
        )}
        <NavigationHeader
          user={user}
          userRole={userRole}
          effectiveRole={effectiveRole}
          profileMenuOpen={profileMenuOpen}
          setProfileMenuOpen={setProfileMenuOpen}
          setViewAsRole={setViewAsRole}
          navigate={navigate}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
          isMobileMenuOpen={isMobileMenuOpen}
          location={location}
          onSignOut={handleSignOut}
        />

        <div className="flex-1 overflow-y-auto py-4">
          <div className="space-y-1 px-3">
            {/* Profile card-like header (compact) for non-PM/Admin only */}
            {user && !showProjectsSection && (
              <div className="mb-3 rounded-lg border border-border p-3">
                <div className="font-semibold text-sm truncate">
                  {((user as any)?.user_metadata?.full_name as string) ||
                    (user?.email?.split('@')[0] ?? '')}
                </div>
                <div className="text-xs text-muted-foreground">
                  {user?.email}
                </div>
              </div>
            )}

            {/* Role-specific primary items */}
            {showProjectsSection ? (
              // Custom simplified PM menu: Inventory and Home here (Projects
              // section below). Inventory leads, because for an admin it is the
              // whole fleet — every location, project and owner in one screen —
              // and it is not reachable from inside any one project.
              <>
                {/* Master inventory: admin view, or PM on at least one project.
                    Login role alone is not enough. */}
                {canSeeMasterInventory && (
                  <SidebarNavLink
                    key="pm-inventory"
                    to="/inventory"
                    icon={Package}
                    label="Inventory"
                    currentPath={location.pathname}
                    onNavigate={() => setIsMobileMenuOpen(false)}
                  />
                )}
                <SidebarNavLink
                  key="pm-home"
                  to="/"
                  icon={Home}
                  label="Home"
                  currentPath={location.pathname}
                  onNavigate={() => setIsMobileMenuOpen(false)}
                />
              </>
            ) : (
              // Non-PM roles: keep existing role-based items
              navigationItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;

                return (
                  <Link
                    key={item.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    to={navItemTarget(item.path, navProjectId)}
                    className={cn(
                      'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground',
                      isActive
                        ? 'bg-accent text-accent-foreground'
                        : 'text-muted-foreground'
                    )}
                  >
                    <Icon className="h-4 w-4" />
                    {item.label}
                  </Link>
                );
              })
            )}

            {/* Projects section for PM, Admin, Scheduler */}
            {showProjectsSection && (
              <div className="mt-3">
                <button
                  className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium hover:bg-accent hover:text-accent-foreground"
                  onClick={() => handleProjectsOpenChange(!projectsOpen)}
                >
                  <span className="flex items-center gap-2">
                    <FolderOpen className="h-4 w-4" /> Projects
                  </span>
                  {projectsOpen ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </button>

                {projectsOpen && (
                  <div className="mt-2 space-y-2">
                    {projectsLoading ? (
                      <div className="px-3 py-6 flex items-center justify-center">
                        <LoaderSpinner />
                      </div>
                    ) : (
                      <>
                        <div className="px-3">
                          <Input
                            placeholder="Search projects..."
                            value={projectSearch}
                            onChange={(e) => setProjectSearch(e.target.value)}
                            className="h-8"
                          />
                        </div>

                        <div className="pr-1">
                          {filteredProjects.map((p) => {
                        // Map status to color
                        const status = String(p?.status || '').toLowerCase();
                        // Get proper status color based on project status
                        const getStatusDotColor = (status: string) => {
                          switch (status?.toLowerCase()) {
                            case 'active':
                              return 'bg-green-500';
                            case 'draft':
                              return 'bg-yellow-500';
                            case 'completed':
                              return 'bg-blue-500';
                            case 'inactive':
                              return 'bg-gray-400';
                            default:
                              return 'bg-gray-400';
                          }
                        };

                        const dotColor = getStatusDotColor(p.status);

                        const isExpanded = expandedProjectId === p.id;
                        return (
                          <div
                            key={p.id}
                            ref={isExpanded ? expandedProjectRef : undefined}
                            className=""
                          >
                            <button
                              type="button"
                              onClick={async () => {
                                // If project is not expanded, expand it and navigate to overview
                                if (!isExpanded) {
                                  handleExpandedProjectChange(p.id);
                                  
                                  // Immediately fetch role if not cached or still loading
                                  const cached = roleCache.get(p.id);
                                  if (!cached || cached.loading) {
                                    // Use optimistic rendering: if user created project, assume project_manager
                                    const isCreator = p.created_by === user?.id;
                                    if (!cached) {
                                      roleCache.set(p.id, {
                                        role: isCreator ? 'project_manager' : null,
                                        hasAccess: true,
                                        loading: true,
                                      });
                                    }
                                    
                                    // Fetch role immediately in background
                                    projectsApi.getProjectRole(p.id).then(response => {
                                      if (response?.data) {
                                        roleCache.set(p.id, {
                                          role: response.data.role,
                                          hasAccess: response.data.hasAccess ?? true,
                                          loading: false,
                                        });
                                        // Force re-render by updating trigger
                                        bumpCacheUpdateTrigger();
                                      }
                                    }).catch(error => {
                                      console.warn(`[Navigation] Failed to fetch role for project ${p.id}:`, error);
                                      // Keep optimistic value
                                      const currentCached = roleCache.get(p.id);
                                      if (currentCached && currentCached.loading) {
                                        roleCache.set(p.id, {
                                          role: currentCached.role,
                                          hasAccess: currentCached.hasAccess,
                                          loading: false,
                                        });
                                      }
                                    });
                                  }
                                  
                                  // Navigate to the project's overview page
                                  navigate(`/projects?projectId=${encodeURIComponent(p.id)}&tab=overview`);
                                  setProjectHandler(p);
                                } else {
                                  // If already expanded, just collapse it
                                  handleExpandedProjectChange(null);
                                }
                              }}
                              className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md hover:bg-accent hover:text-accent-foreground"
                            >
                              <span className="flex items-center gap-2 truncate">
                                <span
                                  className={`inline-block h-2.5 w-2.5 rounded-full ${dotColor}`}
                                />
                                <span className="truncate">{p.name}</span>
                              </span>
                              {isExpanded ? (
                                <ChevronDown className="h-4 w-4 shrink-0" />
                              ) : (
                                <ChevronRight className="h-4 w-4 shrink-0" />
                              )}
                            </button>

                            {isExpanded && (
                              <div className="ml-6 mt-1 mb-2 space-y-0.5">
                                <OverviewTabLink 
                                  projectId={p.id}
                                  project={p}
                                  getCurrentTab={getCurrentTab}
                                  getCurrentProjectId={getCurrentProjectId}
                                  setIsMobileMenuOpen={setIsMobileMenuOpen}
                                  setProjectHandler={setProjectHandler}
                                  location={location}
                                  roleCache={roleCache}
                                  cacheUpdateTrigger={cacheUpdateTrigger}
                                />
                                <ActionCenterTabLink
                                  projectId={p.id}
                                  project={p}
                                  getCurrentTab={getCurrentTab}
                                  getCurrentProjectId={getCurrentProjectId}
                                  setIsMobileMenuOpen={setIsMobileMenuOpen}
                                  setProjectHandler={setProjectHandler}
                                  roleCache={roleCache}
                                  cacheUpdateTrigger={cacheUpdateTrigger}
                                />
                                <InventoryTabLink
                                  projectId={p.id}
                                  project={p}
                                  getCurrentTab={getCurrentTab}
                                  getCurrentProjectId={getCurrentProjectId}
                                  setIsMobileMenuOpen={setIsMobileMenuOpen}
                                  setProjectHandler={setProjectHandler}
                                  roleCache={roleCache}
                                  cacheUpdateTrigger={cacheUpdateTrigger}
                                />
                                <TeamTabLink
                                  projectId={p.id}
                                  project={p}
                                  getCurrentTab={getCurrentTab}
                                  getCurrentProjectId={getCurrentProjectId}
                                  setIsMobileMenuOpen={setIsMobileMenuOpen}
                                  setProjectHandler={setProjectHandler}
                                  roleCache={roleCache}
                                  cacheUpdateTrigger={cacheUpdateTrigger}
                                />
                                <ParticipantsTabLink 
                                  projectId={p.id}
                                  project={p}
                                  getCurrentTab={getCurrentTab}
                                  getCurrentProjectId={getCurrentProjectId}
                                  setIsMobileMenuOpen={setIsMobileMenuOpen}
                                  setProjectHandler={setProjectHandler}
                                  location={location}
                                  roleCache={roleCache}
                                  cacheUpdateTrigger={cacheUpdateTrigger}
                                />
                                <TasksTabLink 
                                  projectId={p.id}
                                  project={p}
                                  getCurrentTab={getCurrentTab}
                                  getCurrentProjectId={getCurrentProjectId}
                                  setIsMobileMenuOpen={setIsMobileMenuOpen}
                                  setProjectHandler={setProjectHandler}
                                  location={location}
                                  roleCache={roleCache}
                                  cacheUpdateTrigger={cacheUpdateTrigger}
                                />
                                <SchedulerTabLink 
                                  projectId={p.id}
                                  project={p}
                                  getCurrentTab={getCurrentTab}
                                  getCurrentProjectId={getCurrentProjectId}
                                  setIsMobileMenuOpen={setIsMobileMenuOpen}
                                  setProjectHandler={setProjectHandler}
                                  roleCache={roleCache}
                                  cacheUpdateTrigger={cacheUpdateTrigger}
                                />
                                <ScheduleTabLink 
                                  projectId={p.id}
                                  project={p}
                                  setIsMobileMenuOpen={setIsMobileMenuOpen}
                                  setProjectHandler={setProjectHandler}
                                  location={location}
                                  roleCache={roleCache}
                                  cacheUpdateTrigger={cacheUpdateTrigger}
                                />
                                <DataUploadsTabLink 
                                  projectId={p.id}
                                  project={p}
                                  getCurrentTab={getCurrentTab}
                                  getCurrentProjectId={getCurrentProjectId}
                                  setIsMobileMenuOpen={setIsMobileMenuOpen}
                                  setProjectHandler={setProjectHandler}
                                  roleCache={roleCache}
                                  cacheUpdateTrigger={cacheUpdateTrigger}
                                />
                                <PaymentApprovalsTabLink 
                                  projectId={p.id}
                                  project={p}
                                  getCurrentTab={getCurrentTab}
                                  getCurrentProjectId={getCurrentProjectId}
                                  setIsMobileMenuOpen={setIsMobileMenuOpen}
                                  setProjectHandler={setProjectHandler}
                                  roleCache={roleCache}
                                  cacheUpdateTrigger={cacheUpdateTrigger}
                                />
                                <ProjectSetupTabLink
                                  projectId={p.id}
                                  project={p}
                                  getCurrentTab={getCurrentTab}
                                  getCurrentProjectId={getCurrentProjectId}
                                  setIsMobileMenuOpen={setIsMobileMenuOpen}
                                  setProjectHandler={setProjectHandler}
                                  roleCache={roleCache}
                                  cacheUpdateTrigger={cacheUpdateTrigger}
                                />
                                <ProjectSettingsTabLink
                                  projectId={p.id}
                                  project={p}
                                  getCurrentTab={getCurrentTab}
                                  getCurrentProjectId={getCurrentProjectId}
                                  setIsMobileMenuOpen={setIsMobileMenuOpen}
                                  setProjectHandler={setProjectHandler}
                                  roleCache={roleCache}
                                  cacheUpdateTrigger={cacheUpdateTrigger}
                                />
                              </div>
                            )}
                          </div>
                        );
                          })}

                          {filteredProjects.length === 0 && (
                            <div className="px-3 py-2 text-xs text-muted-foreground">
                              No projects found
                            </div>
                          )}
                        </div>

                        <div className="px-3">
                          <Link
                            to="/projects?create=new"
                            onClick={() => setIsMobileMenuOpen(false)}
                            className="flex items-center gap-2 text-sm px-2 py-1 rounded-md hover:bg-accent hover:text-accent-foreground"
                          >
                            <Plus className="h-4 w-4" /> Create Project
                          </Link>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Additional PM links: My Calendar, Reports / Dashboards, Help / FAQ - Hidden for all roles per user request */}
            {/* To show these items, change false to true below */}
            {/* eslint-disable-next-line no-constant-binary-expression */}
            {false && (
              <div className="mt-3 space-y-1">
                <SidebarNavLink
                  to="/calendar"
                  icon={Calendar}
                  label="My Calendar"
                  currentPath={location.pathname}
                  onNavigate={() => setIsMobileMenuOpen(false)}
                />
                <SidebarNavLink
                  to="/analytics"
                  icon={BarChart3}
                  label="Reports / Dashboards"
                  currentPath={location.pathname}
                  onNavigate={() => setIsMobileMenuOpen(false)}
                />
                <SidebarNavLink
                  to="/feedback"
                  icon={HelpCircle}
                  label="Help / FAQ"
                  currentPath={location.pathname}
                  onNavigate={() => setIsMobileMenuOpen(false)}
                />
              </div>
            )}
          </div>

          {/* Upload Status Indicator */}
          <UploadStatusIndicator />
        </div>
          </>
        )}
      </nav>
    </>
  );
};

export default Navigation;
